/**
 * 区域选择器（2026-09-06，主公「市民端的案件要素页的区域的选择框优化一下」）
 *
 * ════════ 原来是什么样、为什么要改 ════════
 *
 * 原来是一个原生 `<select>`，24 个区县全塞在里面。在手机上它会调起系统的滚轮，
 * 一次只看得见三四条，找「都江堰市」要盲滚半天；而同一页的「被投诉方」用的是
 * 底部抽屉（`RespondentPicker`），两个控件长得完全不是一路，人会以为它们的规矩不同。
 *
 * ════════ 改了什么、没改什么 ════════
 *
 * ★ **没改的（这条是硬约束，不是体验偏好）**：仍然只能从协会那张表里选，
 *   一个字都不许自由输入。`区域ID` 要推协会老系统，表外的值推送当场失败且删不掉。
 *   原生 select 是靠「结构上就没有输入框」来保证这一点；这里同样没有任何可提交的
 *   自由文本 —— 搜索框只过滤本地列表，选中的一定是列表里的那一条。
 *
 * ★ 改的：
 *   · 一屏铺开 24 条（两列），不必盲滚；
 *   · 带搜索，打两个字就定位；
 *   · 当前选中项高亮，一眼看出「我现在选的是哪个」——原生 select 收起后只剩一行字；
 *   · 与「被投诉方」共用 `PickerSheet`，挂载点、锁滚、Esc、焦点、安全区这些
 *     容易做漏的事不重做一遍。
 *
 * ★ 「不确定/清空」放在底栏而不是列表第一项：它不是一个区域，混进列表里
 *   会被当成「成都市外」那一类真选项。
 */

import { useMemo, useRef, useState } from 'react';
import { PickerSheet } from './PickerSheet';

export interface RegionOption {
  id: number;
  name: string;
}

export function RegionPicker({
  areas,
  valueId,
  valueName,
  disabled,
  onPick,
  onClear,
}: {
  /** 可选区域。`null` ＝ 还没拉到（调用方另有说明与重试，这里只负责禁用） */
  areas: RegionOption[] | null;
  valueId: number | null;
  valueName: string;
  disabled?: boolean;
  onPick: (a: RegionOption) => void;
  onClear: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [kw, setKw] = useState('');
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  const list = useMemo(() => {
    const all = areas ?? [];
    const q = kw.trim();
    if (!q) return all;
    return all.filter((a) => a.name.includes(q));
  }, [areas, kw]);

  const off = Boolean(disabled) || areas === null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`rg-trigger${valueId === null ? ' is-empty' : ''}`}
        disabled={off}
        aria-label="区域"
        onClick={() => {
          setKw('');
          setOpen(true);
        }}
      >
        <span className="rg-trigger-t">{valueId === null ? '请选择' : valueName || '已选择'}</span>
        <span className="rg-trigger-cue" aria-hidden="true" />
      </button>

      <PickerSheet
        open={open}
        title="选择区域"
        anchor={triggerRef.current}
        onClose={() => setOpen(false)}
        autoFocusField=".rg-search"
        footer={
          /* 清空放底栏：它不是一个区域，混进列表会被当成「成都市外」那类真选项。 */
          <button
            type="button"
            className="rg-clear"
            disabled={valueId === null}
            onClick={() => {
              onClear();
              setOpen(false);
            }}
          >
            不确定 / 清除已选
          </button>
        }
      >
        <div className="rg-search-wrap">
          <input
            className="rg-search"
            type="search"
            inputMode="search"
            placeholder="搜区县名，如「武侯」"
            value={kw}
            onChange={(e) => setKw(e.target.value)}
          />
        </div>

        <div className="rp-sheet-scroll rg-scroll">
          {list.length === 0 ? (
            <p className="rg-empty">没有匹配「{kw.trim()}」的区县。协会名录里只有这 24 个。</p>
          ) : (
            <div className="rg-grid">
              {list.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  className={`rg-item${a.id === valueId ? ' is-on' : ''}`}
                  onClick={() => {
                    onPick(a);
                    setOpen(false);
                  }}
                >
                  {a.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </PickerSheet>
    </>
  );
}
